import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  Transition,
  Paper,
} from "@mantine/core";
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { useForm } from "@mantine/form";
import { useNavigate } from "react-router-dom";
import { DashboardEndpoints } from "../../../lib/endpoints-dashboard";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { useToastNotification } from "../../../hooks/useToast";
import { IOrganizationProps } from "../../../types/Organization";
import { Check } from "lucide-react";
import "./styles.scss";

const NewOrganizationPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(
    null
  );
  const [activeStep, setActiveStep] = useState(0);
  const [transitionDirection, setTransitionDirection] = useState<
    "slide-left" | "slide-right"
  >("slide-left");
  const toast = useToastNotification();
  const navigate = useNavigate();

  const form = useForm({
    initialValues: {
      name: "",
      subtitle: "",
      description: "",
      website: "",
      logo: null as FileWithPath | null,
      cover_image: null as FileWithPath | null,
      email: "",
      number_of_locations: "",
      years_of_experience: "",
      instagram_link: "",
      facebook_link: "",
      linkedin_link: "",
      x_link: "",
      youtube_link: "",
      phone_number: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      mission: "",
      vision: "",
      values: "",
      about: "",
    },
    validate: {
      name: (value: string) =>
        value.trim().length < 2 ? "Name must be at least 2 characters" : null,
      subtitle: (value: string) =>
        value.trim().length < 2 ? "Subtitle is required" : null,
      description: (value: string) =>
        value.trim().length < 20
          ? "Description must be at least 20 characters"
          : null,
      about: (value: string) =>
        value.trim().length < 20
          ? "About must be at least 20 characters"
          : null,
      mission: (value: string) =>
        value.trim().length < 10 ? "Mission is required" : null,
      vision: (value: string) =>
        value.trim().length < 10 ? "Vision is required" : null,
      values: (value: string) =>
        value.trim().length < 10 ? "Values are required" : null,
      logo: (value: FileWithPath | null) =>
        !value ? "Logo is required" : null,
      cover_image: (value: FileWithPath | null) =>
        !value ? "Cover image is required" : null,
      email: (value: string) => {
        if (!value.trim()) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? null : "Enter a valid email address";
      },
      website: (value: string) => {
        if (!value.trim()) return "Website is required";
        try {
          new URL(value);
          return null;
        } catch {
          return "Please enter a valid URL";
        }
      },
      number_of_locations: (value: string) =>
        !value.trim() || Number(value) < 1
          ? "Provide the number of locations"
          : null,
      years_of_experience: (value: string) =>
        !value.trim() || Number(value) < 0
          ? "Years of experience must be zero or more"
          : null,
      phone_number: (value: string) =>
        /^\+?[0-9\s-]{7,15}$/.test(value.trim())
          ? null
          : "Enter a valid phone number",
      address: (value: string) =>
        value.trim().length < 4 ? "Address is required" : null,
      city: (value: string) =>
        value.trim().length < 2 ? "City is required" : null,
      state: (value: string) =>
        value.trim().length < 2 ? "State is required" : null,
      country: (value: string) =>
        value.trim().length < 2 ? "Country is required" : null,
      postal_code: (value: string) =>
        value.trim().length < 3 ? "Postal code is required" : null,
      instagram_link: (value: string) => {
        if (!value.trim()) return "Instagram link is required";
        try {
          new URL(value);
          return null;
        } catch {
          return "Enter a valid Instagram URL";
        }
      },
      facebook_link: (value: string) => {
        if (!value.trim()) return "Facebook link is required";
        try {
          new URL(value);
          return null;
        } catch {
          return "Enter a valid Facebook URL";
        }
      },
      linkedin_link: (value: string) => {
        if (!value.trim()) return "LinkedIn link is required";
        try {
          new URL(value);
          return null;
        } catch {
          return "Enter a valid LinkedIn URL";
        }
      },
      x_link: (value: string) => {
        if (!value.trim()) return "X link is required";
        try {
          new URL(value);
          return null;
        } catch {
          return "Enter a valid X URL";
        }
      },
      youtube_link: (value: string) => {
        if (!value.trim()) return "YouTube link is required";
        try {
          new URL(value);
          return null;
        } catch {
          return "Enter a valid YouTube URL";
        }
      },
    },
  });

  useEffect(() => {
    if (form.values.logo) {
      const url = URL.createObjectURL(form.values.logo);
      setLogoPreview(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }

    setLogoPreview(null);
  }, [form.values.logo]);

  useEffect(() => {
    if (form.values.cover_image) {
      const url = URL.createObjectURL(form.values.cover_image);
      setCoverImagePreview(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }

    setCoverImagePreview(null);
  }, [form.values.cover_image]);

  const stepFieldGroups = useMemo(
    () => [
      {
        label: "Identity",
        description: "Tell us who you are and what you stand for.",
        fields: [
          "name",
          "subtitle",
          "description",
          "mission",
          "vision",
          "values",
          "about",
          "logo",
          "cover_image",
        ],
      },
      {
        label: "Presence",
        description: "Share how and where people can reach you.",
        fields: [
          "website",
          "email",
          "phone_number",
          "number_of_locations",
          "years_of_experience",
          "address",
          "city",
          "state",
          "country",
          "postal_code",
        ],
      },
      {
        label: "Digital",
        description: "Connect your social footprint to stay discoverable.",
        fields: [
          "instagram_link",
          "facebook_link",
          "linkedin_link",
          "x_link",
          "youtube_link",
        ],
      },
    ],
    []
  );

  const validateStepFields = (stepIndex: number) => {
    const fields = stepFieldGroups[stepIndex].fields as Array<
      keyof typeof form.values
    >;
    let hasError = false;
    fields.forEach((field) => {
      const result = form.validateField(field);
      if (result.error) {
        hasError = true;
      }
    });
    return !hasError;
  };

  const handleNext = () => {
    if (validateStepFields(activeStep)) {
      setTransitionDirection("slide-left");
      setActiveStep((current) =>
        Math.min(current + 1, stepFieldGroups.length - 1)
      );
    }
  };

  const handlePrev = () => {
    setTransitionDirection("slide-right");
    setActiveStep((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!validateStepFields(activeStep)) {
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (key === "logo" || key === "cover_image") {
          if (value) {
            formData.append(key, value as File);
          }
          return;
        }
        formData.append(key, value as string);
      });

      const endpoint = DashboardEndpoints.CreateOrganization();
      const response = await PerformRequest<IOrganizationProps>({
        method: endpoint.method as any,
        route: endpoint.route,
        data: formData,
      });

      if (response.status === 201) {
        toast.success("Organization created successfully!");
        form.reset();
        setLogoPreview(null);
        setCoverImagePreview(null);
        setActiveStep(0);
        navigate("/d/organizations");
      } else {
        toast.error("Failed to create organization. Please try again.");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="organization-page organization-create-page">
      <div className="organization-page__header">
        <div>
          <Title order={1}>Create Organization</Title>
          <Text c="dimmed" mt="xs">
            Build your organization profile to unlock event tooling.
          </Text>
        </div>
        <Button
          variant="subtle"
          onClick={() => navigate("/d/organizations")}
          disabled={loading}
        >
          Back to organizations
        </Button>
      </div>

      <Paper
        className="organization-create-page__form"
        shadow="xl"
        radius="lg"
        withBorder
      >
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="tw-stepper">
            {stepFieldGroups.map((step, index) => {
              const status =
                index < activeStep
                  ? "complete"
                  : index === activeStep
                  ? "current"
                  : "upcoming";
              return (
                <React.Fragment key={step.label}>
                  <div
                    className={`tw-stepper__item tw-stepper__item--${status}`}
                  >
                    <div className="tw-stepper__indicator">
                      {status === "complete" ? <Check size={18} /> : index + 1}
                    </div>
                    <p className="tw-stepper__label">{step.label}</p>
                  </div>
                  {index < stepFieldGroups.length - 1 && (
                    <div
                      className={`tw-stepper__divider ${
                        index < activeStep
                          ? "tw-stepper__divider--complete"
                          : ""
                      }`}
                    >
                      <span />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          <div className="organization-create-page__panels">
            {stepFieldGroups.map((step, index) => (
              <Transition
                key={step.label}
                mounted={activeStep === index}
                transition={transitionDirection}
                duration={320}
                timingFunction="ease"
              >
                {(styles) => (
                  <div
                    style={styles}
                    className="organization-create-page__panel"
                  >
                    {index === 0 && (
                      <Stack gap="md">
                        <TextInput
                          label="Organization Name"
                          placeholder="Enter organization name"
                          required
                          {...form.getInputProps("name")}
                        />
                        <TextInput
                          label="Subtitle"
                          placeholder="A short tagline"
                          required
                          {...form.getInputProps("subtitle")}
                        />
                        <Textarea
                          label="Description"
                          placeholder="Enter organization description"
                          required
                          minRows={4}
                          {...form.getInputProps("description")}
                        />
                        <Textarea
                          label="About"
                          placeholder="Share additional background"
                          minRows={3}
                          required
                          {...form.getInputProps("about")}
                        />
                        <Group grow>
                          <Textarea
                            label="Mission"
                            placeholder="State your mission"
                            required
                            minRows={2}
                            {...form.getInputProps("mission")}
                          />
                          <Textarea
                            label="Vision"
                            placeholder="Share your vision"
                            required
                            minRows={2}
                            {...form.getInputProps("vision")}
                          />
                        </Group>
                        <Textarea
                          label="Values"
                          placeholder="List your core values"
                          required
                          minRows={2}
                          {...form.getInputProps("values")}
                        />
                        <div>
                          <Text size="sm" fw={500} mb="xs">
                            Logo
                          </Text>
                          {logoPreview ? (
                            <div className="organization-create-page__logo-preview">
                              <img src={logoPreview} alt="Preview" />
                              <Button
                                size="xs"
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                  form.setFieldValue("logo", null);
                                  setLogoPreview(null);
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <Dropzone
                              accept={IMAGE_MIME_TYPE}
                              onDrop={(files) => {
                                if (files.length > 0) {
                                  form.setFieldValue("logo", files[0]);
                                }
                              }}
                              maxSize={5 * 1024 * 1024}
                              multiple={false}
                              className="organization-create-page__dropzone"
                            >
                              <Group
                                justify="center"
                                gap="xl"
                                style={{
                                  minHeight: 140,
                                  pointerEvents: "none",
                                }}
                              >
                                <div>
                                  <Text size="xl" inline>
                                    Drag image here or click to select
                                  </Text>
                                  <Text size="sm" c="dimmed" inline mt={7}>
                                    Upload a logo for your organization (max
                                    5MB)
                                  </Text>
                                </div>
                              </Group>
                            </Dropzone>
                          )}
                        </div>
                        <div>
                          <Text size="sm" fw={500} mb="xs">
                            Cover Image
                          </Text>
                          {coverImagePreview ? (
                            <div className="organization-create-page__logo-preview">
                              <img
                                src={coverImagePreview}
                                alt="Cover Preview"
                              />
                              <Button
                                size="xs"
                                variant="subtle"
                                color="red"
                                onClick={() => {
                                  form.setFieldValue("cover_image", null);
                                  setCoverImagePreview(null);
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <Dropzone
                              accept={IMAGE_MIME_TYPE}
                              onDrop={(files) => {
                                if (files.length > 0) {
                                  form.setFieldValue("cover_image", files[0]);
                                }
                              }}
                              maxSize={5 * 1024 * 1024}
                              multiple={false}
                              className="organization-create-page__dropzone"
                            >
                              <Group
                                justify="center"
                                gap="xl"
                                style={{
                                  minHeight: 140,
                                  pointerEvents: "none",
                                }}
                              >
                                <div>
                                  <Text size="xl" inline>
                                    Drag image here or click to select
                                  </Text>
                                  <Text size="sm" c="dimmed" inline mt={7}>
                                    Upload a cover image for your organization
                                    (max 5MB)
                                  </Text>
                                </div>
                              </Group>
                            </Dropzone>
                          )}
                        </div>
                      </Stack>
                    )}

                    {index === 1 && (
                      <Stack gap="md">
                        <TextInput
                          label="Website"
                          placeholder="https://example.com"
                          required
                          {...form.getInputProps("website")}
                        />
                        <TextInput
                          label="Email"
                          placeholder="contact@example.com"
                          type="email"
                          required
                          {...form.getInputProps("email")}
                        />
                        <TextInput
                          label="Phone Number"
                          placeholder="+234 123 456 7890"
                          required
                          {...form.getInputProps("phone_number")}
                        />
                        <Group grow>
                          <TextInput
                            label="Number of Locations"
                            type="number"
                            required
                            {...form.getInputProps("number_of_locations")}
                          />
                          <TextInput
                            label="Years of Experience"
                            type="number"
                            required
                            {...form.getInputProps("years_of_experience")}
                          />
                        </Group>
                        <TextInput
                          label="Address"
                          placeholder="Street and number"
                          required
                          {...form.getInputProps("address")}
                        />
                        <Group grow>
                          <TextInput
                            label="City"
                            required
                            {...form.getInputProps("city")}
                          />
                          <TextInput
                            label="State"
                            required
                            {...form.getInputProps("state")}
                          />
                        </Group>
                        <Group grow>
                          <TextInput
                            label="Country"
                            required
                            {...form.getInputProps("country")}
                          />
                          <TextInput
                            label="Postal Code"
                            required
                            {...form.getInputProps("postal_code")}
                          />
                        </Group>
                      </Stack>
                    )}

                    {index === 2 && (
                      <Stack gap="md">
                        <TextInput
                          label="Instagram"
                          placeholder="https://instagram.com/your-handle"
                          required
                          {...form.getInputProps("instagram_link")}
                        />
                        <TextInput
                          label="Facebook"
                          placeholder="https://facebook.com/your-page"
                          required
                          {...form.getInputProps("facebook_link")}
                        />
                        <TextInput
                          label="LinkedIn"
                          placeholder="https://linkedin.com/company/your-org"
                          required
                          {...form.getInputProps("linkedin_link")}
                        />
                        <TextInput
                          label="X (Twitter)"
                          placeholder="https://x.com/your-handle"
                          required
                          {...form.getInputProps("x_link")}
                        />
                        <TextInput
                          label="YouTube"
                          placeholder="https://youtube.com/@your-channel"
                          required
                          {...form.getInputProps("youtube_link")}
                        />
                      </Stack>
                    )}
                  </div>
                )}
              </Transition>
            ))}
          </div>

          <Group justify="space-between" mt="xl">
            <Button
              variant="light"
              onClick={
                activeStep === 0
                  ? () => navigate("/d/organizations")
                  : handlePrev
              }
              disabled={loading}
              type="button"
            >
              {activeStep === 0 ? "Back to organizations" : "Previous"}
            </Button>
            {activeStep < stepFieldGroups.length - 1 ? (
              <Button onClick={handleNext} type="button">
                Next step
              </Button>
            ) : (
              <Button type="submit" loading={loading}>
                Create Organization
              </Button>
            )}
          </Group>
        </form>
      </Paper>
    </div>
  );
};

export default NewOrganizationPage;
